import { log } from '../log';
import { isInExcludedAncestor } from './locations/page-link';

// Coalesces a burst of appends - a widget rendering a list of cards arrives as
// many mutations - into a single re-apply.
const DEBOUNCE_MS = 50;

let observer = null;
let debounceTimer = null;

const containsAnchor = node => {
	// 1 === Node.ELEMENT_NODE. Text nodes are the common case here and are the
	// cheapest thing to reject.
	if (node.nodeType !== 1) {
		return false;
	}

	// Anchors the takeover would skip anyway must not wake the observer, or
	// opening an "offer details" popup triggers a re-apply that can do nothing.
	// Same rule as the document search, imported so there is one definition.
	//
	// The href check matches the `a[href]` query below, and both match the
	// takeover's own `a[href^="..."]` selectors: an anchor with no href can
	// never be a target, so waking for one only costs a re-apply that finds
	// nothing.
	if (node.nodeName === 'A') {
		return node.hasAttribute('href') && !isInExcludedAncestor(node);
	}

	const anchors = node.querySelectorAll?.('a[href]');

	return Boolean(
		anchors?.length &&
			Array.from(anchors).some(anchor => !isInExcludedAncestor(anchor))
	);
};

// Only react to *added* anchors. A takeover modifies the anchors it claims in
// place (href, target, click listener, and the text or image inside them), so
// its own mutations never satisfy this predicate and cannot feed back into the
// observer as a loop.
const hasAddedAnchors = mutations =>
	mutations.some(mutation =>
		Array.from(mutation.addedNodes).some(containsAnchor)
	);

export const stopWatchingForLateLinks = () => {
	clearTimeout(debounceTimer);
	debounceTimer = null;

	if (observer) {
		observer.disconnect();
		observer = null;
	}
};

/**
 * Watch for links that render after a takeover pass has already run.
 *
 * A MODIFY_LINKS editor resolves its targets and searches the DOM once. Widgets
 * that render links from an async fetch (ws-specials, ws-inv-listing) can land
 * theirs after that search, and in more than one batch, leaving some links
 * claimed and others not (DE749901).
 *
 * Installed lazily - only once a MODIFY_LINKS edit actually exists - so a page
 * with no link takeover configured never creates an observer. Idempotent: the
 * first caller wins and later calls are no-ops.
 *
 * Deliberately has no time limit. Links can arrive from a user action at any
 * point - ws-specials has a "Load More" button that renders the rest of its
 * promos on click - so any wall-clock window would expire before the click that
 * needs it. The cost is bounded instead by how cheap the work is: the observer
 * only exists on pages with a takeover, the predicate rejects text mutations
 * immediately, and the re-apply is debounced.
 *
 * @param { function } onLateLinks - invoked when new anchors appear.
 * @returns { boolean } whether this call installed the observer.
 */
export const watchForLateLinks = onLateLinks => {
	if (observer || typeof MutationObserver !== 'function' || !document?.body) {
		return false;
	}

	observer = new MutationObserver(mutations => {
		if (!hasAddedAnchors(mutations)) {
			return;
		}

		clearTimeout(debounceTimer);
		debounceTimer = setTimeout(() => {
			log('[LINK_WATCHER] New links on page, re-applying link takeover');
			onLateLinks();
		}, DEBOUNCE_MS);
	});

	observer.observe(document.body, { childList: true, subtree: true });

	return true;
};
